/* ═══════════════════════════════════════════
   PINE PARK ARCADE — Quick Add Modal
   Vanilla JS. No jQuery.
   Opens a variant-selector modal when the user
   clicks "Options" on any multi-variant product card.
   ═══════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Module state ── */
  var modal           = null;
  var backdrop        = null;
  var closeBtn        = null;
  var imageEl         = null;
  var titleEl         = null;
  var purchaseState   = null;
  var optionsArea     = null;
  var priceEl         = null;
  var addBtn          = null;
  var comingSoonState = null;
  var moneyFormat     = '${{amount}}';

  var currentProduct   = null;
  var selectedOptions  = {};   // { "Size": "Large", "Flavor": "Strawberry" }
  var focusTrigger     = null; // element that opened the modal (for focus restore)
  var isAdding         = false;

  /* ══════════════════════════════════════
     HELPERS
     ══════════════════════════════════════ */

  /* Safely escape text before inserting as innerHTML */
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ══════════════════════════════════════
     MONEY FORMATTING
     Uses Shopify's money_format embedded in
     the modal's data-money-format attribute.
     ══════════════════════════════════════ */
  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var amount      = (cents / 100).toFixed(2);
    var amountNoDec = String(Math.round(cents / 100));
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDec);
  }

  /* ══════════════════════════════════════
     SHOPIFY CDN IMAGE SIZING
     Appends a size parameter to Shopify CDN
     image URLs so we don't serve oversized images.
     ══════════════════════════════════════ */
  function sizedImageUrl(src, size) {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    // New CDN (files.shopifycdn.com) uses query params
    if (src.indexOf('cdn.shopify.com/files') !== -1) {
      var sep = src.indexOf('?') !== -1 ? '&' : '?';
      return src + sep + 'width=' + size;
    }
    // Classic CDN — insert _SIZEx before the extension
    return src.replace(/(\.[a-zA-Z]+)(\?.*)?$/, '_' + size + 'x$1$2');
  }

  /* ══════════════════════════════════════
     BODY SCROLL LOCK — iOS-safe
     Saves the current scroll position, fixes
     the body in place, and restores on close.
     ══════════════════════════════════════ */
  function lockScroll() {
    var scrollY = window.scrollY || window.pageYOffset;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top      = '-' + scrollY + 'px';
    document.body.style.width    = '100%';
    document.body.dataset.scrollY = String(scrollY);
  }

  function unlockScroll() {
    var savedY = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top      = '';
    document.body.style.width    = '';
    delete document.body.dataset.scrollY;
    window.scrollTo(0, savedY);
  }

  /* ══════════════════════════════════════
     VARIANT LOOKUP
     Find the variant whose option1/2/3 all
     match selectedOptions.
     ══════════════════════════════════════ */
  function getSelectedVariant() {
    if (!currentProduct) return null;
    var options = currentProduct.options;
    return currentProduct.variants.find(function (v) {
      return options.every(function (optName, i) {
        return v['option' + (i + 1)] === selectedOptions[optName];
      });
    }) || null;
  }

  /* ══════════════════════════════════════
     OPTION VALUE AVAILABILITY
     Returns true if ANY variant using that
     value (and the already-chosen other options)
     is available.
     ══════════════════════════════════════ */
  function isValueAvailable(optionName, value) {
    var options = currentProduct.options;
    var optIdx  = options.indexOf(optionName);
    return currentProduct.variants.some(function (v) {
      if (v['option' + (optIdx + 1)] !== value) return false;
      var othersOk = options.every(function (otherOpt, i) {
        if (otherOpt === optionName) return true;
        if (!selectedOptions[otherOpt]) return true;
        return v['option' + (i + 1)] === selectedOptions[otherOpt];
      });
      return othersOk && v.available;
    });
  }

  /* ══════════════════════════════════════
     RENDER OPTION PILLS
     Builds pill buttons for every option group.
     Each pill shows the value text; unavailable
     ones show a small "SOLD OUT" badge below.
     ══════════════════════════════════════ */
  function renderOptions() {
    optionsArea.innerHTML = '';
    var options = currentProduct.options;

    options.forEach(function (optionName) {
      /* Collect unique values preserving Shopify order */
      var values = [];
      currentProduct.variants.forEach(function (v) {
        var val = v['option' + (options.indexOf(optionName) + 1)];
        if (val && values.indexOf(val) === -1) values.push(val);
      });

      var group = document.createElement('div');
      group.className = 'qam-option-group';

      var label = document.createElement('div');
      label.className = 'qam-option-label';
      label.textContent = '// ' + optionName.toUpperCase();
      group.appendChild(label);

      var pillsWrap = document.createElement('div');
      pillsWrap.className = 'qam-pills';
      pillsWrap.setAttribute('role', 'group');
      pillsWrap.setAttribute('aria-label', optionName);

      values.forEach(function (val) {
        var available  = isValueAvailable(optionName, val);
        var isSelected = selectedOptions[optionName] === val;

        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'qam-pill';
        pill.setAttribute('data-option-name', optionName);
        pill.setAttribute('data-option-value', val);

        if (!available) {
          pill.classList.add('qam-pill--unavailable');
          pill.setAttribute('aria-disabled', 'true');
          pill.setAttribute('tabindex', '-1');
          /* Two-line structure: value text + sold-out badge */
          pill.innerHTML =
            '<span class="qam-pill-value">' + esc(val) + '</span>' +
            '<span class="qam-pill-badge">SOLD OUT</span>';
        } else {
          pill.innerHTML = '<span class="qam-pill-value">' + esc(val) + '</span>';
        }

        if (isSelected) {
          pill.classList.add('qam-pill--selected');
          pill.setAttribute('aria-pressed', 'true');
        } else {
          pill.setAttribute('aria-pressed', 'false');
        }

        pillsWrap.appendChild(pill);
      });

      group.appendChild(pillsWrap);
      optionsArea.appendChild(group);
    });
  }

  /* ══════════════════════════════════════
     UPDATE MODAL STATE
     Re-renders pills, updates price, image,
     and button state on every option change.
     ══════════════════════════════════════ */
  function updateModal() {
    renderOptions();

    var variant = getSelectedVariant();
    var allSelected = currentProduct.options.every(function (opt) {
      return !!selectedOptions[opt];
    });

    /* ── Price ── */
    var priceHTML = '';
    if (variant) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHTML += '<span class="qam-compare-price">' + formatMoney(variant.compare_at_price) + '</span>';
      }
      priceHTML += formatMoney(variant.price);
    } else if (currentProduct.price_varies) {
      priceHTML = 'From ' + formatMoney(currentProduct.price_min);
    } else {
      priceHTML = formatMoney(currentProduct.price);
    }
    priceEl.innerHTML = priceHTML;

    /* ── Variant image swap ── */
    if (variant && variant.featured_image && variant.featured_image.src) {
      var newSrc = sizedImageUrl(variant.featured_image.src, 480);
      if (imageEl.src !== newSrc) {
        imageEl.style.opacity = '0.6';
        imageEl.src = newSrc;
        imageEl.onload = function () { imageEl.style.opacity = '1'; };
      }
    }

    /* ── Button state — never clobber mid-add ── */
    if (isAdding) return;

    if (!allSelected) {
      addBtn.textContent = 'SELECT AN OPTION';
      addBtn.disabled    = true;
      addBtn.className   = 'qam-add-btn';
      addBtn.removeAttribute('data-variant-id');
    } else if (variant && variant.available) {
      addBtn.textContent = 'ADD TO CART';
      addBtn.disabled    = false;
      addBtn.className   = 'qam-add-btn qam-add-btn--ready';
      addBtn.setAttribute('data-variant-id', String(variant.id));
    } else {
      addBtn.textContent = 'SOLD OUT';
      addBtn.disabled    = true;
      addBtn.className   = 'qam-add-btn qam-add-btn--sold-out';
      addBtn.removeAttribute('data-variant-id');
    }
  }

  /* ══════════════════════════════════════
     POPULATE MODAL
     Sets title + image, then shows either the
     normal purchase UI or the coming-soon panel.
     ══════════════════════════════════════ */
  function populateModal(product, isComingSoon) {
    currentProduct  = product;
    selectedOptions = {};
    isAdding        = false;

    /* Title */
    titleEl.textContent = product.title;

    /* Featured image */
    var featSrc = typeof product.featured_image === 'string'
      ? product.featured_image
      : (product.featured_image && product.featured_image.src
          ? product.featured_image.src
          : '');

    imageEl.style.opacity = '1';
    imageEl.src = featSrc ? sizedImageUrl(featSrc, 480) : '';
    imageEl.alt = product.title;

    if (isComingSoon) {
      purchaseState.setAttribute('hidden', '');
      comingSoonState.removeAttribute('hidden');
    } else {
      purchaseState.removeAttribute('hidden');
      comingSoonState.setAttribute('hidden', '');

      /* Preselect: pack variant first (beverages, found by title, never by a
         hardcoded number), else the first available variant when the product
         has exactly one option; products with two or more options keep the
         explicit choice. renderOptions() then marks the pill selected and
         updateModal() enables ADD. */
      var pre = null, pi, pv;
      for (pi = 0; pi < product.variants.length; pi++) {
        pv = product.variants[pi];
        if (pv.available && String(pv.title).toLowerCase().indexOf('pack') !== -1) { pre = pv; break; }
      }
      if (!pre && product.options.length === 1) {
        for (pi = 0; pi < product.variants.length; pi++) {
          if (product.variants[pi].available) { pre = product.variants[pi]; break; }
        }
      }
      if (pre) {
        for (pi = 0; pi < product.options.length; pi++) {
          selectedOptions[product.options[pi]] = pre['option' + (pi + 1)];
        }
      }

      updateModal();
    }
  }

  /* ══════════════════════════════════════
     OPEN MODAL
     ══════════════════════════════════════ */
  function openModal(product, triggerEl, isComingSoon) {
    populateModal(product, isComingSoon);

    focusTrigger = triggerEl || document.activeElement;

    modal.classList.add('qam-open');
    modal.setAttribute('aria-hidden', 'false');

    lockScroll();

    /* Move focus into modal after animation starts */
    setTimeout(function () { closeBtn.focus(); }, 60);
  }

  /* ══════════════════════════════════════
     CLOSE MODAL
     successAnimation: true → scale-up fly-out
     successAnimation: false → scale-down fade
     ══════════════════════════════════════ */
  function closeModal(successAnimation) {
    if (!modal.classList.contains('qam-open')) return;

    var animClass = successAnimation ? 'qam-success-close' : 'qam-closing';
    var duration  = successAnimation ? 310 : 260;

    modal.classList.add(animClass);

    setTimeout(function () {
      modal.classList.remove('qam-open', animClass);
      modal.setAttribute('aria-hidden', 'true');
      isAdding = false;

      unlockScroll();

      /* Restore focus to the trigger element */
      if (focusTrigger && typeof focusTrigger.focus === 'function') {
        focusTrigger.focus();
      }
    }, duration);
  }

  /* ══════════════════════════════════════
     ADD TO CART
     POST /cart/add.js → update count → success
     animation → auto-close.
     ══════════════════════════════════════ */
  function handleAddToCart() {
    var variantId = addBtn.getAttribute('data-variant-id');
    if (!variantId || isAdding) return;

    isAdding = true;
    addBtn.textContent = 'ADDING...';
    addBtn.disabled    = true;
    addBtn.className   = 'qam-add-btn qam-add-btn--adding';

    /* The cart drawer's section parameters ride along, so the response
       carries the re-rendered drawer (assets/cart-drawer.js). */
    var drawer = (window.PinePark && window.PinePark.cartDrawer) || null;
    var body = drawer ? drawer.sectionParams() : {};
    body.id = parseInt(variantId, 10);
    body.quantity = 1;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add.js returned ' + res.status);
        return res.json();
      })
      .then(function (item) {
        var addedItem = item;
        addBtn.textContent = 'ADDED \u2713';
        addBtn.className   = 'qam-add-btn qam-add-btn--success';

        if (!drawer) updateCartCount();

        /* Auto-close with success fly-out after 1.5s. The cart drawer opens
           (or its bar shows) only after closeModal has finished its 310ms
           animation and released the scroll lock; both write
           body.style.position, and nesting them would save scrollY as 0. */
        setTimeout(function () {
          closeModal(true);
          setTimeout(function () {
            if (drawer) drawer.added(addedItem);
          }, 350);
        }, 1500);
      })
      .catch(function (err) {
        console.warn('[QuickAdd] Add to cart failed:', err);
        isAdding = false;
        addBtn.textContent = 'ERROR, TRY AGAIN';
        addBtn.className   = 'qam-add-btn qam-add-btn--sold-out';
        setTimeout(function () {
          var v = getSelectedVariant();
          if (v && v.available) {
            addBtn.textContent = 'ADD TO CART';
            addBtn.disabled    = false;
            addBtn.className   = 'qam-add-btn qam-add-btn--ready';
          }
        }, 2000);
      });
  }

  /* ══════════════════════════════════════
     CART COUNT UPDATE
     Matches the pattern in theme.js.
     ══════════════════════════════════════ */
  function updateCartCount() {
    fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var countEls = document.querySelectorAll('[data-cart-count]');
        if (countEls.length) {
          countEls.forEach(function (el) { el.textContent = cart.item_count; });
        } else {
          var navCart = document.querySelector('.nav-cart');
          if (navCart) navCart.textContent = 'CART [' + cart.item_count + ']';
        }
      })
      .catch(function () {});
  }

  /* ══════════════════════════════════════
     FOCUS TRAP + ESCAPE KEY
     ══════════════════════════════════════ */
  function onKeyDown(e) {
    if (!modal.classList.contains('qam-open')) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal(false);
      return;
    }

    if (e.key === 'Tab') {
      var focusable = Array.from(modal.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), [tabindex="0"]'
      ));
      if (!focusable.length) return;

      var first = focusable[0];
      var last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  /* ══════════════════════════════════════
     INIT
     ══════════════════════════════════════ */
  function init() {
    modal = document.getElementById('quick-add-modal');
    if (!modal) return;

    backdrop        = modal.querySelector('.qam-backdrop');
    closeBtn        = modal.querySelector('#qam-close');
    imageEl         = modal.querySelector('#qam-image');
    titleEl         = modal.querySelector('#qam-title');
    purchaseState   = modal.querySelector('#qam-purchase-state');
    optionsArea     = modal.querySelector('#qam-options-area');
    priceEl         = modal.querySelector('#qam-price');
    addBtn          = modal.querySelector('#qam-add-btn');
    comingSoonState = modal.querySelector('#qam-coming-soon-state');
    moneyFormat     = modal.getAttribute('data-money-format') || '${{amount}}';

    /* ── Open: click [data-quick-add] anywhere on the page ── */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();

      var isComingSoon = btn.getAttribute('data-coming-soon') === 'true';

      var jsonStr = btn.getAttribute('data-product-json');
      if (!jsonStr) {
        /* No JSON and not coming-soon → navigate to product page */
        if (!isComingSoon) {
          var url = btn.getAttribute('data-product-url');
          if (url) window.location.href = url;
        }
        return;
      }

      var product;
      try {
        product = JSON.parse(jsonStr);
      } catch (err) {
        console.warn('[QuickAdd] Failed to parse product JSON:', err);
        var url = btn.getAttribute('data-product-url');
        if (url) window.location.href = url;
        return;
      }

      openModal(product, btn, isComingSoon);
    });

    /* ── Pill selection (delegated to optionsArea) ── */
    optionsArea.addEventListener('click', function (e) {
      var pill = e.target.closest('.qam-pill');
      if (!pill || pill.classList.contains('qam-pill--unavailable')) return;

      var optName = pill.getAttribute('data-option-name');
      var optVal  = pill.getAttribute('data-option-value');
      if (!optName || !optVal) return;

      selectedOptions[optName] = optVal;
      updateModal();
    });

    /* ── Close on backdrop click ── */
    backdrop.addEventListener('click', function () {
      closeModal(false);
    });

    /* ── Close button ── */
    closeBtn.addEventListener('click', function () {
      closeModal(false);
    });

    /* ── Add to cart ── */
    addBtn.addEventListener('click', function () {
      if (!addBtn.disabled && addBtn.classList.contains('qam-add-btn--ready')) {
        handleAddToCart();
      }
    });

    /* ── Focus trap + Escape ── */
    document.addEventListener('keydown', onKeyDown);
  }

  document.addEventListener('DOMContentLoaded', init);

})();
